import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Badge,
    Button,
    Col,
    Container,
    Form,
    Row,
} from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import InstitutionLogo from '@/components/common/InstitutionLogo';
import ConfirmModal from '@/components/common/ConfirmModal';
import ActionNotice, {
    useActionNotice,
} from '@/components/common/ActionNotice';
import { useAuth } from '@/shared/contexts/AuthContext';
import {
    OPEN_STATUSES,
    STATUS_LABELS,
    STATUS_VARIANTS,
} from '@/shared/constants/listings';
import {
    MESSAGE_MAX_LENGTH,
    REQUEST_OPEN_STATUSES,
    REQUEST_STATUS_LABELS,
    REQUEST_STATUS_VARIANTS,
} from '@/shared/constants/requests';
import listingService from '@/shared/services/listingService';
import requestService from '@/shared/services/requestService';
import { serverMessage } from '@/shared/utils/serverMessage';
import CompleteExchangeModal from '@/features/listings/components/CompleteExchangeModal';
import usePageTitle from '@/shared/hooks/usePageTitle';

const ACTION_FAILED = 'Could not complete that action. Try again.';

function Fact({ label, value }) {
    if (value === undefined || value === null || value === '') {
        return null;
    }
    return (
        <>
            <dt className="col-sm-4">{label}</dt>
            <dd className="col-sm-8">{value}</dd>
        </>
    );
}

/** Name, campus, and an address only when the reader is not that person. */
function Party({ heading, person, showEmail }) {
    if (!person) {
        return null;
    }
    return (
        <>
            <h2 className="h6">{heading}</h2>
            <p className="mb-1">
                {person.name}
                <InstitutionLogo
                    institution={person.institution}
                    className="ms-1"
                />
            </p>
            {person.department && (
                <p className="text-muted mb-1">{person.department}</p>
            )}
            {showEmail && person.email && (
                <p className="mb-1">
                    <a href={`mailto:${person.email}`}>{person.email}</a>
                </p>
            )}
            {showEmail && person.phone && (
                <p className="mb-3">{person.phone}</p>
            )}
        </>
    );
}

function RequestThreadPage() {
    const { id } = useParams();
    const { userProfile } = useAuth();
    const [request, setRequest] = useState(null);
    const [owner, setOwner] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const {
        ref: noticeRef,
        notice,
        error: actionError,
        report,
    } = useActionNotice();
    const [body, setBody] = useState('');
    const [sending, setSending] = useState(false);
    const [confirming, setConfirming] = useState('');
    // Named after the item once it is read, so a member holding two threads
    // open can tell the tabs apart.
    usePageTitle(
        request?.listing?.title
            ? `Request for ${request.listing.title}`
            : 'Request'
    );

    const load = useCallback(async () => {
        const doc = await requestService.get(id);
        setRequest(doc);
        const listingId = doc.listing?._id;
        if (!listingId) {
            return;
        }
        try {
            // The listing projection on a request carries its owner as a bare
            // id, so the listing itself is what names the other party and
            // gives the requester an address to reach them at.
            const listing = await listingService.get(listingId);
            setOwner(listing.owner);
        } catch (_error) {
            setOwner(null);
        }
    }, [id]);

    useEffect(() => {
        async function first() {
            setLoading(true);
            setError('');
            try {
                await load();
            } catch (_error) {
                setRequest(null);
                setError(
                    'Request not found. It may have been removed, or it may' +
                        ' not be yours to read.'
                );
            } finally {
                setLoading(false);
            }
        }
        first();
    }, [load]);

    // Success is reported as loudly as failure, and focus moves to the
    // sentence either way. Every one of these actions removes the button that
    // was clicked, which drops focus to the body and restarts tabbing at the
    // skip link.
    const runAction = async (act, done) => {
        setConfirming('');
        try {
            await act();
            report({ notice: done });
        } catch (caught) {
            report({ error: serverMessage(caught, ACTION_FAILED) });
        }
        try {
            // Reread whether or not it worked. A refusal almost always means
            // the other party moved first, so the page that offered the
            // action is already showing a state that no longer exists and
            // retrying from it could never succeed.
            await load();
        } catch (_error) {
            // The request is unreadable now; the message above still stands.
        }
    };

    const handleSend = async (event) => {
        event.preventDefault();
        if (!body.trim()) {
            return;
        }
        setSending(true);
        try {
            setRequest(await requestService.addMessage(id, body.trim()));
            setBody('');
        } catch (caught) {
            // Announced where it is, without the focus move: the message box
            // still holds what the member typed and is where they will try
            // again from, so taking focus out of it costs more than the
            // region's own announcement is worth.
            report({
                error: serverMessage(
                    caught,
                    'Could not send that message. Try again.'
                ),
                focus: false,
            });
        } finally {
            setSending(false);
        }
    };

    if (loading) {
        return (
            <Container className="py-4">
                <LoadingSpinner message="Loading request" />
            </Container>
        );
    }
    if (error || !request) {
        return (
            <Container className="py-4">
                <Alert variant="danger">{error}</Alert>
                <Link to="/requests/mine">Back to my requests</Link>
            </Container>
        );
    }

    const listing = request.listing || {};
    const requester = request.requester;
    const viewerId = userProfile?._id;
    const isAdmin = (userProfile?.roles || []).includes('admin');
    const sameAsViewer = (person) =>
        Boolean(viewerId) && String(person) === String(viewerId);
    const isRequester = sameAsViewer(requester._id);
    const isOwner = sameAsViewer(listing.owner?._id || listing.owner);
    // An admin stands in for the owner on both tiers, which is the same rule
    // the server applies to accept and decline.
    const decides = isOwner || isAdmin;
    const requestOpen = request.status === 'open';
    // Every action the server gates on the listing has to be gated on the
    // listing here. Completing an exchange leaves the request `accepted` and
    // moves only the listing, so deriving the closing actions from the
    // request alone re-offers both of them the moment they succeed, and
    // expiry closes a listing without closing its requests, which would leave
    // Accept on offer against a LISTING_NOT_OPEN refusal.
    const listingOpen = OPEN_STATUSES.includes(listing.status);
    const canClose =
        listing.status === 'claimed' && request.status === 'accepted';
    const canMessage = REQUEST_OPEN_STATUSES.includes(request.status);
    const messages = request.messages;

    return (
        <Container className="py-4">
            {/* The thread is the request, so it still reads when the
                listing behind it has gone. */}
            <h1 className="h3 mb-1">
                {listing._id ? (
                    <Link to={`/listings/${listing._id}`}>{listing.title}</Link>
                ) : (
                    'This listing is no longer available.'
                )}
            </h1>
            {listing._id && (
                <div className="mb-3">
                    <InstitutionLogo
                        institution={listing.institution}
                        className="me-1"
                    />
                    <Badge bg={STATUS_VARIANTS[listing.status]}>
                        {STATUS_LABELS[listing.status] || listing.status}
                    </Badge>
                </div>
            )}
            <ActionNotice ref={noticeRef} notice={notice} error={actionError} />
            <Row className="g-4">
                <Col lg={7}>
                    <h2 className="h5">Messages</h2>
                    {/* Announced politely: a message arriving while the other
                        party is mid-sentence must not interrupt them. */}
                    <div
                        role="log"
                        aria-live="polite"
                        aria-label="Messages"
                        className="mb-3"
                    >
                        {messages.length === 0 ? (
                            <p className="text-muted mb-0">
                                No messages yet. Say hello.
                            </p>
                        ) : (
                            <ul className="list-unstyled mb-0">
                                {messages.map((message) => (
                                    <li key={message._id} className="mb-3">
                                        <div className="d-flex gap-2 align-items-center">
                                            <span className="fw-semibold">
                                                {message.author?.name}
                                            </span>
                                            <InstitutionLogo
                                                institution={
                                                    message.author?.institution
                                                }
                                            />
                                            <time
                                                dateTime={message.sentAt}
                                                className="text-muted small"
                                            >
                                                {new Date(
                                                    message.sentAt
                                                ).toLocaleString()}
                                            </time>
                                        </div>
                                        <p className="mb-0">{message.body}</p>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                    <Form onSubmit={handleSend}>
                        <Form.Group controlId="thread-message" className="mb-2">
                            <Form.Label>Your message</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={body}
                                maxLength={MESSAGE_MAX_LENGTH}
                                disabled={!canMessage}
                                aria-describedby="thread-message-count"
                                onChange={(event) =>
                                    setBody(event.target.value)
                                }
                            />
                            <Form.Text id="thread-message-count">
                                {body.length} of {MESSAGE_MAX_LENGTH} characters
                            </Form.Text>
                        </Form.Group>
                        {!canMessage && (
                            <p className="text-muted">
                                This request is closed, so messaging is off.
                            </p>
                        )}
                        <Button type="submit" disabled={!canMessage || sending}>
                            Send message
                        </Button>
                    </Form>
                </Col>
                <Col lg={5}>
                    <h2 className="h5">This request</h2>
                    <dl className="row mb-4">
                        <dt className="col-sm-4">Status</dt>
                        <dd className="col-sm-8">
                            <Badge bg={REQUEST_STATUS_VARIANTS[request.status]}>
                                {REQUEST_STATUS_LABELS[request.status] ||
                                    request.status}
                            </Badge>
                        </dd>
                        <Fact label="Note" value={request.note} />
                        <Fact
                            label="Preferred window"
                            value={request.preferredWindow}
                        />
                        <Fact label="Deliver to" value={request.deliverTo} />
                    </dl>
                    {/* Named as a region because both parties also appear
                        as message authors, and the reader needs one place
                        that answers who is on the other end. */}
                    <section aria-label="Who is involved" className="mb-3">
                        <Party
                            heading="Requested by"
                            person={requester}
                            showEmail={!isRequester}
                        />
                        <Party
                            heading="Posted by"
                            person={owner}
                            showEmail={!isOwner}
                        />
                    </section>
                    <div className="d-flex gap-2 flex-wrap">
                        {isRequester && requestOpen && (
                            <Button
                                variant="outline-secondary"
                                onClick={() => setConfirming('withdraw')}
                            >
                                Withdraw request
                            </Button>
                        )}
                        {decides && requestOpen && listingOpen && (
                            <Button
                                onClick={() =>
                                    runAction(
                                        () => requestService.accept(id),
                                        'Request accepted. The item is' +
                                            ' claimed and every other request' +
                                            ' on it is declined.'
                                    )
                                }
                            >
                                Accept
                            </Button>
                        )}
                        {/* Declining has no listing check on the server, so a
                            listing that expired with requests against it can
                            still be cleared down. */}
                        {decides && requestOpen && (
                            <Button
                                variant="outline-secondary"
                                onClick={() =>
                                    runAction(
                                        () => requestService.decline(id),
                                        'Request declined.'
                                    )
                                }
                            >
                                Decline
                            </Button>
                        )}
                        {/* Either party may close a claimed exchange, because
                            either one may be the person standing in the room
                            when the item changes hands. Only the owner may
                            put it back on offer. */}
                        {canClose && (decides || isRequester) && (
                            <Button onClick={() => setConfirming('complete')}>
                                Mark completed
                            </Button>
                        )}
                        {canClose && decides && (
                            <Button
                                variant="outline-secondary"
                                onClick={() => setConfirming('release')}
                            >
                                Release claim
                            </Button>
                        )}
                    </div>
                </Col>
            </Row>
            <CompleteExchangeModal
                show={confirming === 'complete'}
                listingTitle={listing.title}
                onCancel={() => setConfirming('')}
                onConfirm={(outcome) =>
                    runAction(
                        () => listingService.complete(listing._id, outcome),
                        'The exchange is closed out.'
                    )
                }
            />
            <ConfirmModal
                show={confirming === 'withdraw'}
                title="Withdraw this request?"
                confirmLabel="Withdraw it"
                cancelLabel="Keep it"
                onCancel={() => setConfirming('')}
                onConfirm={() =>
                    runAction(
                        () => requestService.withdraw(id),
                        'Your request is withdrawn.'
                    )
                }
            >
                <p className="mb-0">
                    The poster stops seeing it and you can ask again later while
                    the item is still open.
                </p>
            </ConfirmModal>
            <ConfirmModal
                show={confirming === 'release'}
                title="Release the claim?"
                confirmLabel="Release the claim"
                cancelLabel="Keep the claim"
                onCancel={() => setConfirming('')}
                onConfirm={() =>
                    runAction(
                        () => listingService.release(listing._id),
                        'The claim is released and the item is back on offer.'
                    )
                }
            >
                <p className="mb-0">
                    {listing.title} goes back on offer and this request is
                    declined.
                </p>
            </ConfirmModal>
        </Container>
    );
}

export default RequestThreadPage;
